import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronLeft, ChevronRight, FileText, Folder, FolderOpen, Plus, Search, X,
} from 'lucide-react';
import { api, type KbTreeNode } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useKbTree } from '../../lib/kb-tree';
import { ancestorPathsOf, useProjectTree } from '../../lib/project-tree';
import { useResizablePanel } from '../../lib/resizable';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Input } from '../ui/input';
import { ResizeHandle } from '../ui/resize-handle';
import { RowMenu } from '../ui/row-menu';

const EXPANDED_KEY = 'kb.tree.expanded';

/**
 * One knowledge-base page in the panel's tree.
 *
 * Keyed by `projectId:path` rather than by path alone: expansion is persisted across projects, and
 * paths are project-relative, so two projects can both hold "auth/sessions" and a bare path would
 * collapse their expansion state into one.
 */
function PageRow({
  node, projectId, activePath, expanded, onToggle, onSelect, onAddPage, canCreatePage, depth,
}: {
  node: KbTreeNode;
  projectId: string;
  activePath: string | null;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onSelect: (path: string) => void;
  onAddPage: (parentPath: string | null) => void;
  /** `KB_CREATE_PAGE`. Threaded rather than read per row, so the tree resolves it once. */
  canCreatePage: boolean;
  depth: number;
}) {
  const key = `${projectId}:${node.path}`;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(key);
  const isActive = node.path === activePath;
  const Icon = hasChildren ? (isOpen ? FolderOpen : Folder) : FileText;

  return (
    <>
      <div
        className={cn(
          'group/row flex items-center rounded-md transition-colors',
          isActive ? 'bg-orange-500 text-white' : 'hover:bg-surface-subtle',
        )}
        style={{ paddingLeft: depth * 12 }}
      >
        <button
          onClick={() => hasChildren && onToggle(key)}
          className={cn(
            'w-4 h-6 flex items-center justify-center flex-shrink-0',
            hasChildren ? (isActive ? 'text-orange-100' : 'text-ink-faint hover:text-ink') : 'opacity-0 cursor-default',
          )}
          tabIndex={hasChildren ? 0 : -1}
          aria-label={hasChildren ? (isOpen ? 'Collapse' : 'Expand') : undefined}
        >
          {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <button
          onClick={() => onSelect(node.path)}
          className="flex-1 flex items-center gap-1.5 py-1.5 min-w-0 text-left"
        >
          <Icon size={12} className={cn('flex-shrink-0', isActive ? 'text-orange-100' : 'text-ink-faint')} />
          <span className={cn('text-xs truncate', isActive ? 'text-white font-medium' : 'text-ink')}>
            {node.title}
          </span>
          {node.pendingChanges > 0 && (
            <span
              title={`${node.pendingChanges} change${node.pendingChanges === 1 ? '' : 's'} awaiting review`}
              className={cn(
                'flex-shrink-0 text-[9px] font-semibold px-1 rounded-full',
                isActive ? 'bg-white/25 text-white' : 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400',
              )}
            >
              {node.pendingChanges}
            </span>
          )}
        </button>
        {/* Creating a page is this menu's only item, so without the capability the whole control goes —
            an empty popover is worse than no button. */}
        {canCreatePage && (
          <RowMenu
            label={`Actions for ${node.title}`}
            tone={isActive ? 'active' : 'default'}
            items={[{ label: 'New page inside', onClick: () => onAddPage(node.path) }]}
          />
        )}
      </div>
      {isOpen &&
        node.children.map((child) => (
          <PageRow
            key={child.id}
            node={child}
            projectId={projectId}
            activePath={activePath}
            expanded={expanded}
            onToggle={onToggle}
            onSelect={onSelect}
            onAddPage={onAddPage}
            canCreatePage={canCreatePage}
            depth={depth + 1}
          />
        ))}
    </>
  );
}

/**
 * The pages panel of one project's knowledge base — the second column of the KB view, between
 * the app sidebar and the document.
 *
 * The page tree used to be nested inside every project row of the app sidebar, which put two
 * different kinds of thing in one list: the hierarchy of clients and projects that people
 * navigate by, and the structure of the documents inside one of them. Producers navigate by
 * client and project and should not have to read a page tree to do it, so the sidebar is
 * projects only and the tree lives here, shown only once you are inside a project's knowledge
 * base. It is entered from the project overview's **Open** or the project row's **Knowledge
 * base**, both of which name the project first.
 *
 * It renders the drag handle to its right as well as the panel, so the page that mounts it gets a
 * resizable column without knowing how wide it is.
 */
export function KbTreePanel({
  activePath, onSelect,
}: {
  activePath: string | null;
  onSelect: (path: string) => void;
}) {
  const navigate = useNavigate();
  const { projectId, projectSlug, tree, allNodes, error, reload } = useKbTree();
  const { allProjects } = useProjectTree();
  const { hasFeature, hasCapability } = useAuth();
  const canCreatePage = hasCapability('KB_CREATE_PAGE');

  const project = useMemo(() => allProjects.find((p) => p.id === projectId) ?? null, [allProjects, projectId]);

  // Deep branches and long titles are unreadable at nav width, so the panel is widenable and
  // remembers its width — the same treatment the app sidebar gets.
  const panel = useResizablePanel({ initial: 264, min: 200, max: 480, side: 'left', storageKey: 'kb.tree.width' });

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(EXPANDED_KEY);
      return new Set<string>(stored ? (JSON.parse(stored) as string[]) : []);
    } catch {
      return new Set<string>();
    }
  });

  const persist = useCallback((next: Set<string>) => {
    setExpanded(next);
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify([...next]));
    } catch {
      // A full or unavailable localStorage costs the memory of which branches were open, which
      // is not worth breaking navigation over.
    }
  }, []);

  const toggle = (key: string) => {
    const next = new Set(expanded);
    if (!next.delete(key)) next.add(key);
    persist(next);
  };

  // Opening the branch that leads to the current page, so a deep link shows it in context rather
  // than a collapsed tree with the selection hidden inside it.
  useEffect(() => {
    if (!activePath || !projectId) return;
    setExpanded((prev) => {
      const keys = ancestorPathsOf(activePath).map((p) => `${projectId}:${p}`);
      const missing = keys.filter((k) => !prev.has(k));
      return missing.length === 0 ? prev : new Set([...prev, ...missing]);
    });
  }, [activePath, projectId]);

  // A flat match list rather than a filtered tree: a match deep in a collapsed branch would
  // otherwise be invisible, and the path under each hit is what tells two same-titled pages apart.
  const [filter, setFilter] = useState('');
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return null;
    return allNodes.filter((n) => n.title.toLowerCase().includes(q) || n.path.toLowerCase().includes(q));
  }, [filter, allNodes]);

  // ─── Create a page ─────────────────────────────────────────────────────────
  const [pageParent, setPageParent] = useState<string | null | undefined>(undefined);
  const [pageTitle, setPageTitle] = useState('');
  const [pageSummary, setPageSummary] = useState('');
  const [pageBusy, setPageBusy] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const openCreatePage = (parentPath: string | null) => {
    setPageParent(parentPath);
    setPageTitle('');
    setPageSummary('');
    setPageError(null);
  };

  const submitCreatePage = async () => {
    if (pageParent === undefined || !projectId || !pageTitle.trim()) return;
    setPageBusy(true);
    setPageError(null);
    try {
      const created = await api.kb.createNode(projectId, {
        title: pageTitle.trim(),
        summary: pageSummary.trim() || undefined,
        parentPath: pageParent ?? undefined,
        // A page nested under another is a section of it; a top-level one is a page in its own
        // right — the same distinction the KB's own authoring rules make.
        kind: pageParent ? 'SECTION' : 'PAGE',
      });
      reload();
      setPageParent(undefined);
      navigate(`/p/${projectSlug}/kb/${created.path}`);
    } catch (e) {
      setPageError(e instanceof Error ? e.message : String(e));
    } finally {
      setPageBusy(false);
    }
  };

  return (
    <>
      <aside
        className="flex-shrink-0 h-full bg-card border-r border-border flex flex-col overflow-hidden"
        style={{ width: panel.width }}
      >
        <div className="px-3 pt-3 pb-2.5 border-b border-border space-y-2.5">
          {/* The project this knowledge base belongs to, and the way back to it. The panel exists
              *inside* a project, and this is what keeps that legible once the sidebar no longer
              nests the pages under the project row. */}
          <Link
            to={`/p/${projectSlug}`}
            className="flex items-center gap-1 text-[11px] text-ink-faint hover:text-ink transition-colors min-w-0"
          >
            <ChevronLeft size={11} className="flex-shrink-0" />
            <span className="truncate">{project?.name ?? 'Project'}</span>
          </Link>

          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-ink truncate">Knowledge base</span>
            {canCreatePage && (
              <button
                onClick={() => openCreatePage(null)}
                title="New page"
                aria-label="New page"
                className="w-6 h-6 flex items-center justify-center rounded-md text-ink-faint hover:bg-surface-muted hover:text-ink transition-colors flex-shrink-0"
              >
                <Plus size={14} />
              </button>
            )}
          </div>

          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter pages…"
              className="w-full pl-7 pr-7 py-1.5 text-xs bg-surface rounded-md border-none outline-none placeholder:text-ink-faint text-ink"
            />
            {filter && (
              <button
                onClick={() => setFilter('')}
                aria-label="Clear filter"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink"
              >
                <X size={11} />
              </button>
            )}
          </div>

          {tree && (
            <div className="flex items-center gap-2 text-[10px] text-ink-faint">
              <span>{tree.nodeCount} page{tree.nodeCount === 1 ? '' : 's'}</span>
              {tree.pendingChanges > 0 && (
                // Plain text rather than a link when KB Review is switched off for this user — the
                // count is still worth knowing, but the destination isn't theirs.
                hasFeature('KB_REVIEW') ? (
                  <Link to="/kb-review" className="text-amber-600 dark:text-amber-400 font-medium hover:underline">
                    {tree.pendingChanges} awaiting review
                  </Link>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400 font-medium">
                    {tree.pendingChanges} awaiting review
                  </span>
                )
              )}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 py-2">
          {error && <p className="px-2 py-1 text-[11px] text-danger-text">{error}</p>}

          {!tree || !projectId ? (
            <p className="px-2 py-4 text-xs text-ink-faint">Loading…</p>
          ) : tree.nodeCount === 0 ? (
            <div className="text-center py-8 px-2">
              <p className="text-xs text-ink-faint mb-2">Nothing written yet.</p>
              <p className="text-[11px] text-ink-faint leading-relaxed">
                Drop a document on the project page and the agent writes the first pages
                {canCreatePage ? ', or ' : '.'}
                {canCreatePage && (
                  <button onClick={() => openCreatePage(null)} className="text-orange-500 hover:text-orange-700">
                    create one by hand
                  </button>
                )}
                {canCreatePage && '.'}
              </p>
            </div>
          ) : filtered ? (
            filtered.length === 0 ? (
              <p className="px-2 py-4 text-xs text-ink-faint">No matches</p>
            ) : (
              <div className="space-y-0.5">
                {filtered.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => onSelect(n.path)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 rounded-md transition-colors',
                      n.path === activePath ? 'bg-orange-500 text-white' : 'hover:bg-surface-subtle',
                    )}
                  >
                    <span className={cn('block text-xs font-medium truncate', n.path === activePath ? 'text-white' : 'text-ink')}>
                      {n.title}
                    </span>
                    <span className={cn('block text-[10px] font-mono truncate', n.path === activePath ? 'text-orange-100' : 'text-ink-faint')}>
                      {n.path}
                    </span>
                  </button>
                ))}
              </div>
            )
          ) : (
            <div className="space-y-0.5">
              {tree.nodes.map((n) => (
                <PageRow
                  key={n.id}
                  node={n}
                  projectId={projectId}
                  activePath={activePath}
                  expanded={expanded}
                  onToggle={toggle}
                  onSelect={onSelect}
                  onAddPage={openCreatePage}
                  canCreatePage={canCreatePage}
                  depth={0}
                />
              ))}
            </div>
          )}
        </div>
      </aside>

      <ResizeHandle handleProps={panel.handleProps} onDoubleClick={panel.reset} />

      {/* Portalled to the body so the dialog sits above the fixed app sidebar and its rail rather
          than inside this panel's stacking context. */}
      {pageParent !== undefined &&
        createPortal(
          <Dialog
            open
            onClose={() => setPageParent(undefined)}
            title={pageParent ? 'New page' : `New page in ${project?.name ?? 'this project'}`}
          >
            <div className="space-y-4">
              {pageParent && (
                <p className="text-xs text-ink-muted">
                  Filed under <span className="font-mono text-ink">{pageParent}</span> in{' '}
                  <span className="text-ink">{project?.name ?? 'this project'}</span>
                </p>
              )}
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">Title</label>
                <Input
                  autoFocus
                  value={pageTitle}
                  onChange={(e) => setPageTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitCreatePage()}
                  placeholder="Persistence layer"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">
                  Summary <span className="text-ink-faint font-normal">(one line — what retrieval routes on)</span>
                </label>
                <Input value={pageSummary} onChange={(e) => setPageSummary(e.target.value)} />
              </div>
              {pageError && (
                <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-1.5">
                  {pageError}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button onClick={() => setPageParent(undefined)}>Cancel</Button>
                <Button variant="primary" onClick={submitCreatePage} disabled={pageBusy || !pageTitle.trim()}>
                  {pageBusy ? 'Creating…' : 'Create'}
                </Button>
              </div>
            </div>
          </Dialog>,
          document.body,
        )}
    </>
  );
}
